import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useMonitoringStore } from '@/store/monitoringStore';
import type {
  Instruction,
  InstructionListResponse,
  MonitoringFilters,
  PaginationMeta,
} from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAYMENTS_KEY = 'payments';
const REFETCH_INTERVAL = 5_000; // 5 seconds
const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Build query params from filter state
// ---------------------------------------------------------------------------

function buildParams(filters: MonitoringFilters): Record<string, string> {
  const params: Record<string, string> = {
    page: String(filters.page),
    limit: String(filters.limit),
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
  };
  if (filters.status !== 'all') params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.currency) params.currency = filters.currency;
  return params;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseMonitoringReturn {
  data: Instruction[];
  pagination: PaginationMeta | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refresh: () => void;
  isRefreshing: boolean;
}

export function useMonitoring(): UseMonitoringReturn {
  const queryClient = useQueryClient();

  // Read all filter fields from store
  const status = useMonitoringStore((s) => s.status);
  const search = useMonitoringStore((s) => s.search);
  const date_from = useMonitoringStore((s) => s.date_from);
  const date_to = useMonitoringStore((s) => s.date_to);
  const currency = useMonitoringStore((s) => s.currency);
  const page = useMonitoringStore((s) => s.page);
  const limit = useMonitoringStore((s) => s.limit);
  const sort_by = useMonitoringStore((s) => s.sort_by);
  const sort_order = useMonitoringStore((s) => s.sort_order);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]);

  const filters: MonitoringFilters = {
    status,
    search: debouncedSearch,
    date_from,
    date_to,
    currency,
    page,
    limit,
    sort_by,
    sort_order,
  };

  const params = buildParams(filters);

  const query = useQuery<InstructionListResponse>({
    queryKey: [PAYMENTS_KEY, params],
    queryFn: () => api.get<InstructionListResponse>('/api/payments', { params }),
    refetchInterval: REFETCH_INTERVAL,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: [PAYMENTS_KEY] });
  };

  return {
    data: query.data?.data ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refresh,
    isRefreshing: query.isFetching && !query.isLoading,
  };
}
